import { forwardRef } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@helios/ui';

interface NavFilterProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  /** Wired to the results list for screen readers. */
  controlsId: string;
  inputId: string;
}

/**
 * Filter-as-you-type across every destination the user may reach.
 *
 * With the operational destinations behind seven domains, typing is the fastest route to
 * any of them; the command palette (⌘K) covers records as well, this covers
 * navigation without leaving the sidebar.
 */
export const NavFilter = forwardRef<HTMLInputElement, NavFilterProps>(
  ({ value, onChange, onClear, controlsId, inputId }, ref) => (
    <div className="px-1 pb-1.5">
      <label htmlFor={inputId} className="sr-only">
        Filter navigation
      </label>
      <Input
        id={inputId}
        ref={ref}
        type="text"
        role="searchbox"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Filter…"
        autoComplete="off"
        spellCheck={false}
        aria-controls={controlsId}
        aria-describedby={`${inputId}-hint`}
        className="h-7 px-2"
        startAdornment={<Search className="h-3.5 w-3.5" aria-hidden="true" />}
        endAdornment={
          value ? (
            <button
              type="button"
              onClick={onClear}
              aria-label="Clear navigation filter"
              className="hover:text-foreground focus-visible:ring-ring flex h-4 w-4 items-center justify-center rounded focus-visible:outline-none focus-visible:ring-2"
            >
              <X className="h-3 w-3" aria-hidden="true" />
            </button>
          ) : null
        }
      />
      <span id={`${inputId}-hint`} className="sr-only">
        Type to filter destinations. Press the down arrow to move into the results.
      </span>
    </div>
  ),
);
NavFilter.displayName = 'NavFilter';
